import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import tw from 'twin.macro';
import styled from 'styled-components';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useHistory } from 'react-router-dom';
import {
    fetchFoods, addFood, updateFood, deleteFood,
    fetchMarkets, addMarket, updateMarket, deleteMarket,
    fetchFeatured, setFeatured, deleteFeatured,
    fetchCategories, addCategory, updateCategory, deleteCategory,
    updateTransaction, deleteTransaction,
    fetchUserByEmail, updateUser, deleteUser,
    fetchCoBuyGroups, updateCoBuyGroup, deleteCoBuyGroup, createCoBuyGroup,
    fetchCoBuyInterests, updateCoBuyInterest, deleteCoBuyInterest,
    fetchPartners, updatePartner, deletePartner,
    fetchNewsletterSubscriptions, deleteNewsletterSubscription,
    fetchContactMessages, deleteContactMessage,
    fetchBlogPosts, addBlogPost, updateBlogPost, deleteBlogPost,
    fetchReviews, updateReview, deleteReview,
    fetchNews, addNews, updateNews, deleteNews,
    fetchAppSettings, updateAppSetting, addAppSetting,
    fetchServedStates
} from 'DBServices/firestoreDBService';
import { fetchCategoryAction, fetchFoodsAction, fetchMarketAction, fetchFeaturedAction } from 'services/actions';
import AdminProductForm from 'components/admin/AdminProductForm';
import AdminCustomerForm from 'components/admin/AdminCustomerForm';
import AdminOrderForm from 'components/admin/AdminOrderForm';
import AdminCoBuyForm from 'components/admin/AdminCoBuyForm';
import AdminCoBuyCreate from 'components/admin/AdminCoBuyCreate';
import AdminInterestForm from 'components/admin/AdminInterestForm';
import ServedAreasManager from 'components/admin/ServedAreasManager';
import AdminClearDataModal from 'components/admin/AdminClearDataModal';
import app from 'services/firebase';
import { fetchTransactions, fetchUsers } from 'services/backendCalls';
import { APP_NAME, SUPER_ADMIN_EMAIL, ROLE_SUB_ADMIN, ROLE_USER } from '../constants';
import cogoToast from 'cogo-toast';

// ─── Styled ──────────────────────────────────────────────────────────────────
const Page = tw.div`min-h-screen bg-gray-100 flex flex-col md:flex-row`;

// Sidebar Layout components
const Sidebar = tw.div`w-full md:w-64 md:fixed top-0 bottom-0 left-0 bg-gray-900 text-gray-300 flex flex-col p-4 md:p-6 flex-shrink-0 border-r border-gray-800 z-10`;
const SidebarHeader = tw.div`mb-4 md:mb-8 flex justify-between items-center`;
const Logo = tw.h1`text-xl font-black text-white flex items-center gap-2 tracking-wider`;
const SidebarNav = tw.nav`flex flex-row md:flex-col gap-2 flex-none md:flex-1 overflow-x-auto md:overflow-y-auto pb-2 md:pb-0 whitespace-no-wrap`;
const SidebarLink = styled.button`
    ${tw`flex items-center gap-2 md:gap-3 px-3 py-2 md:px-4 md:py-3 rounded-lg text-sm font-semibold transition duration-200 text-left`}
    ${({ active }) => active
        ? tw`bg-green-600 text-white shadow-md`
        : tw`hover:bg-gray-800 hover:text-white`}
`;
const SidebarFooter = tw.div`mt-auto pt-6 border-t border-gray-800 flex flex-col gap-3`;
const UserInfo = tw.div`flex flex-col`;
const UserEmail = tw.span`text-xs text-gray-500 font-semibold truncate`;
const SignOutButton = tw.button`w-full bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2 px-4 rounded transition duration-200`;

const MainContent = tw.main`flex-1 p-6 pb-24 md:p-10 md:pb-32 md:ml-64`;
const ContentHeader = tw.div`flex items-center justify-between mb-6 pb-4 border-b border-gray-200 flex-wrap gap-4`;
const PageTitle = tw.h2`text-2xl font-bold text-gray-800`;

const SectionHeader = tw.div`flex items-center justify-between mb-4 flex-wrap gap-4`;
const SectionTitle = tw.h2`text-lg font-bold text-gray-700`;
const AddBtn = tw.button`bg-green-500 text-white px-4 py-2 rounded font-semibold text-sm hover:bg-green-600 transition`;
const StyledTable = tw.table`w-full bg-white rounded-lg shadow overflow-hidden text-sm`;
const TableContainer = tw.div`overflow-x-auto w-full`;
const Table = ({ children, ...props }) => <TableContainer><StyledTable {...props}>{children}</StyledTable></TableContainer>;
const Th = tw.th`text-left px-4 py-3 bg-green-100 text-gray-600 font-semibold border-b border-gray-200`;
const Td = tw.td`px-4 py-3 border-b border-gray-100 text-gray-700 align-middle`;
const EditBtn = tw.button`text-blue-500 hover:text-blue-700 font-semibold mr-3`;
const DeleteBtn = tw.button`text-red-400 hover:text-red-600 font-semibold`;

const Badge = styled.span`
    ${tw`inline-block px-2 py-1 rounded text-xs font-bold`} 
    ${({ status }) => {
        if (status === 'success' || status === 'delivered' || status === 'pay_on_delivery_success') return tw`bg-green-100 text-green-700`;
        if (status === 'pending' || status === 'processing' || status === 'in_transit' || status === 'dispatching' || status === 'active' || status === 'ordered' || status === 'pay_on_delivery_pending') return tw`bg-teal-100 text-teal-700`;
        if (status === 'failed' || status === 'cancelled' || status === 'refunded') return tw`bg-red-100 text-red-700`;
        return tw`bg-gray-100 text-gray-500`;
    }}
`;

const EmptyRow = tw.tr``;
const EmptyTd = tw.td`px-4 py-6 text-center text-gray-400 italic`;
const StatusBar = tw.div`mb-4 text-sm text-gray-500`;
const ErrorMsg = tw.div`mb-4 text-sm text-red-500 bg-red-100 px-4 py-2 rounded`;
const Spinner = tw.div`text-center py-12 text-gray-400`;
const ThumbImg = styled.img`${tw`w-10 h-10 object-cover rounded`}`;
const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontSize: '0.95rem'
};
const FeaturedNote = tw.p`text-xs text-gray-400 mt-1 mb-4`;
const PaginationContainer = tw.div`flex items-center justify-between mt-4`;
const PaginationButton = tw.button`px-4 py-2 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed`;
const PageInfo = tw.span`text-gray-700`;

// Dashboard overview grids
const MetricsGrid = tw.div`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8`;
const MetricCard = tw.div`bg-white rounded-lg shadow p-6 flex items-center justify-between hover:shadow-md transition`;
const MetricLabel = tw.p`text-xs text-gray-500 font-semibold uppercase tracking-wider`;
const MetricValue = tw.h3`text-2xl font-bold text-gray-800 mt-1`;
const MetricIconContainer = styled.div`
    ${tw`p-3 rounded-full`} 
    ${({ color }) => {
        if (color === 'green') return tw`bg-green-100 text-green-600`;
        if (color === 'blue') return tw`bg-blue-100 text-blue-600`;
        if (color === 'amber') return tw`bg-teal-100 text-teal-600`;
        return tw`bg-indigo-100 text-indigo-600`;
    }}
`;

const ChartsGrid = tw.div`grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8`;
const ChartCard = tw.div`bg-white rounded-lg shadow p-6 flex flex-col`;
const ChartTitle = tw.h3`text-sm font-bold text-gray-700 mb-4 border-b border-gray-100 pb-2`;

// Filtering row
const FilterRow = tw.div`flex flex-wrap gap-4 mb-6 items-center justify-between bg-white p-4 rounded-lg shadow text-sm`;
const SearchInput = tw.input`border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-green-500 w-full sm:w-64`;
const SelectFilter = tw.select`border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-green-500`;
const RoleSelect = styled.select`
    ${tw`border border-gray-300 rounded px-2 py-1 text-xs font-semibold bg-white focus:outline-none focus:border-green-500`}
`;

const TABS = ['Overview', 'Foods', 'Market', 'Featured', 'Category', 'Partners', 'Orders', 'Customers', 'Payments', 'Delivery', 'Co-Buying', 'Newsletter', 'Messages', 'Blog', 'Reviews', 'News', 'Locations', 'Settings'];

const auth = getAuth(app);

// Sidebar Icons map
const TabIcons = {
    Overview: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4zM14 16a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2v-4z" />
        </svg>
    ),
    Foods: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m0-12.728l.707.707m12.728 12.728l.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
        </svg>
    ),
    Market: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
    ),
    Featured: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
    ),
    Category: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
    ),
    Orders: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
    ),
    Customers: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
    ),
    Payments: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
    ),
    Delivery: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0zM13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1" />
        </svg>
    ),
    'Co-Buying': (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
    ),
    Locations: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
    ),
    Partners: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
    ),
    News: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
    ),
    Settings: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
    ),
    Newsletter: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
    ),
    Messages: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
    ),
    Blog: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
    ),
    Reviews: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
    )
};

// ─── Revenue line chart and other helper charts ──────────────────────────────
const RevenueLineChart = ({ data, labelStep = 1 }) => {
    if (!data || data.length === 0) return <div style={{ height: 180 }} />;
    const width = 500;
    const height = 180;
    const paddingLeft = 60;
    const paddingRight = 20;
    const paddingTop = 20;
    const paddingBottom = 30;

    const maxVal = Math.max(...data.map(d => d.revenue), 1000);
    const roundedMax = Math.ceil(maxVal / 1000) * 1000;

    const chartWidth = width - paddingLeft - paddingRight;
    const chartHeight = height - paddingTop - paddingBottom;

    const points = data.map((d, index) => {
        const x = paddingLeft + (index / (data.length - 1)) * chartWidth;
        const y = paddingTop + chartHeight - (d.revenue / roundedMax) * chartHeight;
        return { x, y, label: d.day, val: d.revenue };
    });

    const pathD = points.reduce((acc, p, i) => {
        return i === 0 ? `M ${p.x} ${p.y}` : `${acc} L ${p.x} ${p.y}`;
    }, '');

    const fillD = points.length > 0
        ? `${pathD} L ${points[points.length - 1].x} ${paddingTop + chartHeight} L ${points[0].x} ${paddingTop + chartHeight} Z`
        : '';

    return (
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%">
            <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#10b981" stopOpacity="0.0" />
                </linearGradient>
            </defs>
            {[0, 0.25, 0.5, 0.75, 1].map((r, i) => {
                const y = paddingTop + r * chartHeight;
                const val = roundedMax - r * roundedMax;
                return (
                    <g key={i}>
                        <line x1={paddingLeft} y1={y} x2={width - paddingRight} y2={y} stroke="#e5e7eb" strokeDasharray="4 4" />
                        <text x={paddingLeft - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#9ca3af">₦{val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}</text>
                    </g>
                );
            })}
            {points.map((p, i) => (
                i % labelStep === 0 || i === points.length - 1 ? (
                    <text key={i} x={p.x} y={height - 8} textAnchor="middle" fontSize="10" fill="#6b7280">{p.label}</text>
                ) : null
            ))}
            {fillD && <path d={fillD} fill="url(#revenueGrad)" />}
            {pathD && <path d={pathD} fill="none" stroke="#10b981" strokeWidth="3" />}
            {points.map((p, i) => (
                <g key={i}>
                    <circle cx={p.x} cy={p.y} r="4" fill="#ffffff" stroke="#10b981" strokeWidth="2" />
                    <title>{`${p.label}: ₦${p.val.toLocaleString()}`}</title>
                </g>
            ))}
        </svg>
    );
};

const OrderStatusBarChart = ({ counts }) => {
    const data = [
        { label: 'Paid', val: counts.success, color: '#10b981' },
        { label: 'Pending', val: counts.pending, color: '#f59e0b' },
        { label: 'Cancelled', val: counts.cancelled, color: '#ef4444' },
        { label: 'Failed', val: counts.failed, color: '#7f1d1d' },
        { label: 'Refunded', val: counts.refunded, color: '#3b82f6' }
    ];

    const width = 500;
    const height = 180;
    const paddingLeft = 40;
    const paddingRight = 20;
    const paddingTop = 20;
    const paddingBottom = 30;

    const maxVal = Math.max(...data.map(d => d.val), 5);
    const roundedMax = Math.ceil(maxVal / 5) * 5;

    const chartWidth = width - paddingLeft - paddingRight;
    const chartHeight = height - paddingTop - paddingBottom;
    const barWidth = (chartWidth / data.length) * 0.6;
    const colWidth = chartWidth / data.length;

    return (
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%">
            {[0, 0.25, 0.5, 0.75, 1].map((r, i) => {
                const y = paddingTop + r * chartHeight;
                const val = Math.round(roundedMax - r * roundedMax);
                return (
                    <g key={i}>
                        <line x1={paddingLeft} y1={y} x2={width - paddingRight} y2={y} stroke="#e5e7eb" strokeDasharray="4 4" />
                        <text x={paddingLeft - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#9ca3af">{val}</text>
                    </g>
                );
            })}
            {data.map((d, i) => {
                const x = paddingLeft + i * colWidth + (colWidth - barWidth) / 2;
                const barHeight = (d.val / roundedMax) * chartHeight;
                const y = paddingTop + chartHeight - barHeight;

                return (
                    <g key={i}>
                        <rect x={x} y={y} width={barWidth} height={barHeight} rx="3" fill={d.color} opacity="0.85" />
                        <text x={x + barWidth / 2} y={y - 5} textAnchor="middle" fontSize="10" fontWeight="bold" fill="#374151">{d.val}</text>
                        <text x={paddingLeft + i * colWidth + colWidth / 2} y={height - 8} textAnchor="middle" fontSize="10" fill="#6b7280">{d.label}</text>
                        <title>{`${d.label}: ${d.val} orders`}</title>
                    </g>
                );
            })}
        </svg>
    );
};

const DeliveryProgressRing = ({ counts }) => {
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    const delivered = counts.delivered || 0;
    const pct = total > 0 ? Math.round((delivered / total) * 100) : 0;

    const size = 150;
    const strokeWidth = 12;
    const radius = (size - strokeWidth) / 2;
    const circ = 2 * Math.PI * radius;
    const strokeDashoffset = circ - (pct / 100) * circ;

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', flexWrap: 'wrap', gap: '1rem', height: '100%' }}>
            <div style={{ position: 'relative', width: size, height: size }}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
                    <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#10b981" strokeWidth={strokeWidth} strokeDasharray={circ} strokeDashoffset={strokeDashoffset} strokeLinecap="round" />
                </svg>
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '1.4rem', fontWeight: 800, color: '#111827' }}>{pct}%</span>
                    <span style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 600 }}>Delivered</span>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#10b981' }}></span>
                    <span style={{ color: '#4b5563' }}>Delivered: <strong>{counts.delivered}</strong></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#f59e0b' }}></span>
                    <span style={{ color: '#4b5563' }}>In Transit: <strong>{counts.in_transit}</strong></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#3b82f6' }}></span>
                    <span style={{ color: '#4b5563' }}>Dispatching: <strong>{counts.dispatching}</strong></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#6b7280' }}></span>
                    <span style={{ color: '#4b5563' }}>Pending: <strong>{counts.pending}</strong></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#ef4444' }}></span>
                    <span style={{ color: '#4b5563' }}>Failed: <strong>{counts.failed}</strong></span>
                </div>
            </div>
        </div>
    );
};

// ─── Main Component ─────────────────────────────────────────────────────────
export default function AdminDashboard() {
    const history = useHistory();
    const [user, setUser] = useState(null);
    const [dbUser, setDbUser] = useState(null);
    const [authChecked, setAuthChecked] = useState(false);
    const [activeTab, setActiveTab] = useState('Overview');
    const [foods, setFoods] = useState([]);
    const [markets, setMarkets] = useState([]);
    const [featured, setFeaturedList] = useState([]);
    const [categories, setCategories] = useState([]);
    const [orders, setOrders] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [payments, setPayments] = useState([]);
    const [delivery, setDelivery] = useState([]);
    const [coBuyGroups, setCoBuyGroups] = useState([]);
    const [coBuyInterests, setCoBuyInterests] = useState([]);
    const [partners, setPartners] = useState([]);
    const [newsletterSubscriptions, setNewsletterSubscriptions] = useState([]);
    const [contactMessages, setContactMessages] = useState([]);
    const [blogPosts, setBlogPosts] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [blogFormOpen, setBlogFormOpen] = useState(false);
    const [editBlog, setEditBlog] = useState(null);
    const [blogFormData, setBlogFormData] = useState({
        title: '',
        description: '',
        imageSrc: '',
        author: '',
        category: '',
        link: '',
        pricingText: '',
        locationText: '',
        rating: ''
    });
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Filters and search states
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [deliveryStatusFilter, setDeliveryStatusFilter] = useState('all');
    const [revenueRange, setRevenueRange] = useState('day');

    // CRUD Modals state
    const [formOpen, setFormOpen] = useState(false); // ProductForm
    const [customerFormOpen, setCustomerFormOpen] = useState(false);
    const [orderFormOpen, setOrderFormOpen] = useState(false);
    const [coBuyFormOpen, setCoBuyFormOpen] = useState(false);
    const [coBuyCreateOpen, setCoBuyCreateOpen] = useState(false);
    const [interestFormOpen, setInterestFormOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [editCustomer, setEditCustomer] = useState(null);
    const [editOrder, setEditOrder] = useState(null);
    const [editCoBuyGroup, setEditCoBuyGroup] = useState(null);
    const [editInterest, setEditInterest] = useState(null);

    // Clear / Reset Data Modal State
    const [clearDataModalOpen, setClearDataModalOpen] = useState(false);
    const [clearDataTarget, setClearDataTarget] = useState(null);

    const openClearModal = (target = null) => {
        setClearDataTarget(target);
        setClearDataModalOpen(true);
    };

    // App settings (VAT, etc.)
    const [appSettings, setAppSettings] = useState([]);
    const [vatInput, setVatInput] = useState('');

    // News articles state
    const [newsArticles, setNewsArticles] = useState([]);
    const [newsFormOpen, setNewsFormOpen] = useState(false);
    const [editNews, setEditNews] = useState(null);

    // Served states & neighborhoods (admin-managed delivery/service areas)
    const [servedStates, setServedStates] = useState([]);
    const [newsFormData, setNewsFormData] = useState({
        title: '', excerpt: '', url: '', imageSrc: '',
        category: '', source: '', publishedAt: '', published: true
    });

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    // ── Auth guard ────────────────────────────────────────────────────────────
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (u) => {
            if (!u) {
                setUser(null);
                setDbUser(null);
                setAuthChecked(true);
                history.replace('/login');
                return;
            }
            setUser(u);
            try {
                const firestoreUser = await fetchUserByEmail(u.email);
                setDbUser(firestoreUser);
                
                const isSuper = u.email === SUPER_ADMIN_EMAIL;
                const isSub = firestoreUser?.role === ROLE_SUB_ADMIN;
                
                if (!isSuper && !isSub) {
                    cogoToast.error("Access Denied: Admin privileges required.");
                    history.replace('/');
                } else {
                    setAuthChecked(true);
                }
            } catch (err) {
                console.error("Auth verification failed", err);
                cogoToast.error("Authentication verification failed.");
                history.replace('/');
            }
        });
        return unsub;
    }, [history]);

    // Super admin is granted by email only and cannot be assigned via the UI.
    const isSuperAdmin = !!user && user.email === SUPER_ADMIN_EMAIL;

    // ── Loaders ───────────────────────────────────────────────────────────────
    const loadFoods = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const f = await fetchFoods();
            setFoods(f);
            if (f) dispatch(fetchFoodsAction(f));
        }
        catch (e) { setError('Failed to load foods: ' + e.message); }
        finally { setLoading(false); }
    }, [dispatch]);

    const loadMarkets = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const m = await fetchMarkets();
            setMarkets(m);
            if (m) dispatch(fetchMarketAction(m));
        }
        catch (e) { setError('Failed to load market items: ' + e.message); }
        finally { setLoading(false); }
    }, [dispatch]);

    const loadFeatured = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const ft = await fetchFeatured();
            setFeaturedList(ft);
            if (ft) dispatch(fetchFeaturedAction(ft));
        }
        catch (e) { setError('Failed to load featured items: ' + e.message); }
        finally { setLoading(false); }
    }, [dispatch]);

    const loadCategories = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const cats = await fetchCategories();
            setCategories(cats);
            if (cats) dispatch(fetchCategoryAction(cats));
        }
        catch (e) { setError('Failed to load categories: ' + e.message); }
        finally { setLoading(false); }
    }, [dispatch]);

    const loadOrders = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const allOrders = await fetchTransactions();
            setOrders(allOrders);
        }
        catch (e) { setError('Failed to load orders: ' + e.message); }
        finally { setLoading(false); }
    }, []);

    const loadCustomers = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const allCustomers = await fetchUsers();
            setCustomers(allCustomers);
        }
        catch (e) { setError('Failed to load customers: ' + e.message); }
        finally { setLoading(false); }
    }, []);

    const loadPayments = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const allPayments = await fetchTransactions();
            setPayments(allPayments);
        }
        catch (e) { setError('Failed to load payments: ' + e.message); }
        finally { setLoading(false); }
    }, []);

    const loadDelivery = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const allDeliveries = await fetchTransactions();
            setDelivery(allDeliveries);
        }
        catch (e) { setError('Failed to load deliveries: ' + e.message); }
        finally { setLoading(false); }
    }, []);

    const loadCoBuyGroups = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const data = await fetchCoBuyGroups();
            setCoBuyGroups(data);
        } catch (e) {
            setError('Failed to load co-buying groups: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadCoBuyInterests = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const data = await fetchCoBuyInterests();
            setCoBuyInterests(data);
        } catch (e) {
            setError('Failed to load co-buying interests: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadPartners = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const data = await fetchPartners();
            setPartners(data);
        } catch (e) {
            setError('Failed to load partners: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadNewsletterSubscriptions = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const data = await fetchNewsletterSubscriptions();
            setNewsletterSubscriptions(data);
        } catch (e) {
            setError('Failed to load newsletter subscriptions: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadContactMessages = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const data = await fetchContactMessages();
            setContactMessages(data);
        } catch (e) {
            setError('Failed to load contact messages: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadBlogPosts = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const data = await fetchBlogPosts();
            setBlogPosts(data);
        } catch (e) {
            setError('Failed to load blog posts: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadReviews = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const data = await fetchReviews();
            setReviews(data);
        } catch (e) {
            setError('Failed to load reviews: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadAppSettings = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const data = await fetchAppSettings();
            setAppSettings(data);
            const vatSetting = data.find(s => s.key === 'vat_percentage');
            if (vatSetting) setVatInput(String(vatSetting.value ?? 7.5));
        } catch (e) {
            setError('Failed to load app settings: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadNews = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const data = await fetchNews();
            setNewsArticles(data);
        } catch (e) {
            setError('Failed to load news: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadServedStates = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const data = await fetchServedStates();
            setServedStates(data);
        } catch (e) {
            setError('Failed to load served areas: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Live counts for all clearable datasets
    const collectionCounts = {
        foods: foods.length,
        categories: categories.length,
        locations: servedStates.length,
        markets: markets.length,
        featured: featured.length,
        orders: orders.length,
        co_buy_groups: coBuyGroups.length,
        co_buy_interests: coBuyInterests.length,
        partners: partners.length,
        reviews: reviews.length,
        news: newsArticles.length,
        blog: blogPosts.length,
        messages: contactMessages.length,
        newsletter: newsletterSubscriptions.length,
    };

    const handleClearDataSuccess = async (clearedKeys = []) => {
        if (clearedKeys.includes('foods')) await loadFoods();
        if (clearedKeys.includes('categories')) await loadCategories();
        if (clearedKeys.includes('locations')) await loadServedStates();
        if (clearedKeys.includes('markets')) await loadMarkets();
        if (clearedKeys.includes('featured')) await loadFeatured();
        if (clearedKeys.includes('orders')) {
            await loadOrders();
            await loadPayments();
            await loadDelivery();
        }
        if (clearedKeys.includes('co_buy_groups')) await loadCoBuyGroups();
        if (clearedKeys.includes('co_buy_interests')) await loadCoBuyInterests();
        if (clearedKeys.includes('partners')) await loadPartners();
        if (clearedKeys.includes('reviews')) await loadReviews();
        if (clearedKeys.includes('news')) await loadNews();
        if (clearedKeys.includes('blog')) await loadBlogPosts();
        if (clearedKeys.includes('messages')) await loadContactMessages();
        if (clearedKeys.includes('newsletter')) await loadNewsletterSubscriptions();
    };

    useEffect(() => {
        if (!authChecked || !user) return;
        if (activeTab === 'Overview') {
            loadOrders();
            loadCustomers();
            loadCoBuyGroups();
            loadCoBuyInterests();
        }
        if (activeTab === 'Foods') loadFoods();
        if (activeTab === 'Market') loadMarkets();
        if (activeTab === 'Featured') loadFeatured();
        if (activeTab === 'Category') loadCategories();
        if (activeTab === 'Orders') loadOrders();
        if (activeTab === 'Customers') loadCustomers();
        if (activeTab === 'Payments') loadPayments();
        if (activeTab === 'Delivery') loadDelivery();
        if (activeTab === 'Co-Buying') {
            loadCoBuyGroups();
            loadCoBuyInterests();
        }
        if (activeTab === 'Partners') loadPartners();
        if (activeTab === 'Newsletter') loadNewsletterSubscriptions();
        if (activeTab === 'Messages') loadContactMessages();
        if (activeTab === 'Blog') loadBlogPosts();
        if (activeTab === 'Reviews') loadReviews();
        if (activeTab === 'News') loadNews();
        if (activeTab === 'Locations') loadServedStates();
        if (activeTab === 'Settings') loadAppSettings();
    }, [activeTab, authChecked, user, loadFoods, loadMarkets, loadFeatured, loadCategories, loadOrders, loadCustomers, loadPayments, loadDelivery, loadCoBuyGroups, loadCoBuyInterests, loadPartners, loadNewsletterSubscriptions, loadContactMessages, loadBlogPosts, loadReviews, loadNews, loadServedStates, loadAppSettings]);

    // ── Metrics Calculations ──────────────────────────────────────────────────
    const getMetrics = () => {
        const totalRevenue = orders
            .filter(o => o.status === 'success')
            .reduce((sum, o) => sum + Number(o.total || 0), 0);

        const totalOrders = orders.length;

        const pendingDeliveries = orders.filter(o => {
            const dStatus = o.deliveryStatus || (o.status === 'delivered' ? 'delivered' : 'pending');
            return dStatus !== 'delivered' && dStatus !== 'failed' && dStatus !== 'cancelled';
        }).length;

        const activeCustomers = customers.length;

        return { totalRevenue, totalOrders, pendingDeliveries, activeCustomers };
    };

    const getRevenueTrendData = (range = 'day') => {
        let days = [];
        const revenueMap = {};
        let labelStep = 1;

        if (range === 'day') {
            for (let i = 6; i >= 0; i--) {
                const date = new Date();
                date.setDate(date.getDate() - i);
                const dateString = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                days.push(dateString);
                revenueMap[dateString] = 0;
            }
            labelStep = 1;
        } else if (range === 'month') {
            for (let i = 29; i >= 0; i--) {
                const date = new Date();
                date.setDate(date.getDate() - i);
                const dateString = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                days.push(dateString);
                revenueMap[dateString] = 0;
            }
            labelStep = 5;
        } else if (range === '3months') {
            for (let i = 89; i >= 0; i--) {
                const date = new Date();
                date.setDate(date.getDate() - i);
                const dateString = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                days.push(dateString);
                revenueMap[dateString] = 0;
            }
            labelStep = 10;
        }

        orders.forEach(ord => {
            if (ord.status === 'success' && ord.createdAt) {
                const orderDate = new Date(ord.createdAt);
                const orderDateStr = orderDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                if (orderDateStr in revenueMap) {
                    revenueMap[orderDateStr] += Number(ord.total || 0);
                }
            }
        });

        return { days: days.map(day => ({ day, revenue: revenueMap[day] })), labelStep };
    };

    const getOrderStatusData = () => {
        const counts = { success: 0, pending: 0, failed: 0, cancelled: 0, refunded: 0 };
        orders.forEach(ord => {
            const s = ord.status || 'pending';
            if (s in counts) {
                counts[s]++;
            } else {
                counts.pending++;
            }
        });
        return counts;
    };

    const getDeliveryStatusData = () => {
        const counts = { pending: 0, dispatching: 0, in_transit: 0, delivered: 0, failed: 0 };
        orders.forEach(ord => {
            const s = ord.deliveryStatus || (ord.status === 'delivered' ? 'delivered' : 'pending');
            if (s in counts) {
                counts[s]++;
            } else {
                counts.pending++;
            }
        });
        return counts;
    };

    const metrics = getMetrics();
    const revenueTrendData = getRevenueTrendData(revenueRange);
    const revenueTrend = revenueTrendData.days;
    const revenueLabelStep = revenueTrendData.labelStep;
    const orderStatuses = getOrderStatusData();
    const deliveryStatuses = getDeliveryStatusData();

    // ── Filtering Logic ───────────────────────────────────────────────────────
    const getFilteredItems = () => {
        let list = [];
        switch (activeTab) {
            case 'Foods': list = foods; break;
            case 'Market': list = markets; break;
            case 'Featured': list = featured; break;
            case 'Category': list = categories; break;
            case 'Orders': list = orders; break;
            case 'Customers': list = customers; break;
            case 'Payments': list = payments; break;
            case 'Delivery': list = delivery; break;
            case 'Co-Buying': list = coBuyGroups; break;
            case 'Partners': list = partners; break;
            case 'Newsletter': list = newsletterSubscriptions; break;
            case 'Messages': list = contactMessages; break;
            case 'Blog': list = blogPosts; break;
            case 'Reviews': list = reviews; break;
            default: list = [];
        }

        return list.filter(item => {
            const matchesSearch = () => {
                if (!searchTerm.trim()) return true;
                const s = searchTerm.toLowerCase();

                if (activeTab === 'Customers') {
                    const fullName = `${item.FirstName || ''} ${item.LastName || ''}`.toLowerCase();
                    return fullName.includes(s) ||
                        (item.Email || '').toLowerCase().includes(s) ||
                        (item.Phone || '').toLowerCase().includes(s) ||
                        (item.address || item.deliveryAddress || '').toLowerCase().includes(s);
                }
                if (activeTab === 'Orders' || activeTab === 'Payments' || activeTab === 'Delivery') {
                    return (item.reference || '').toLowerCase().includes(s) ||
                        (item.userName || '').toLowerCase().includes(s) ||
                        (item.userEmail || '').toLowerCase().includes(s) ||
                        (item.driver || '').toLowerCase().includes(s);
                }
                if (activeTab === 'Co-Buying') {
                    return (item.name || '').toLowerCase().includes(s) ||
                        (item.neighborhood || '').toLowerCase().includes(s) ||
                        (item.bulkItem || '').toLowerCase().includes(s);
                }
                if (activeTab === 'Partners') {
                    return (item.businessName || '').toLowerCase().includes(s) ||
                        (item.name || '').toLowerCase().includes(s) ||
                        (item.email || '').toLowerCase().includes(s) ||
                        (item.partnerType || '').toLowerCase().includes(s);
                }
                if (activeTab === 'Newsletter') {
                    return (item.email || '').toLowerCase().includes(s);
                }
                if (activeTab === 'Messages') {
                    return (item.name || '').toLowerCase().includes(s) ||
                        (item.email || '').toLowerCase().includes(s) ||
                        (item.message || '').toLowerCase().includes(s);
                }
                if (activeTab === 'Blog') {
                    return (item.title || '').toLowerCase().includes(s) ||
                        (item.description || '').toLowerCase().includes(s) ||
                        (item.author || '').toLowerCase().includes(s);
                }
                if (activeTab === 'Reviews') {
                    return (item.foodName || '').toLowerCase().includes(s) ||
                        (item.userName || '').toLowerCase().includes(s) ||
                        (item.comment || '').toLowerCase().includes(s) ||
                        (item.status || '').toLowerCase().includes(s);
                }
                // Product tabs
                return (item.title || '').toLowerCase().includes(s) ||
                    (item.content || '').toLowerCase().includes(s);
            };

            const matchesStatus = () => {
                if (activeTab === 'Orders' || activeTab === 'Payments') {
                    if (statusFilter === 'all') return true;
                    return (item.status || 'pending') === statusFilter;
                }
                if (activeTab === 'Delivery') {
                    if (deliveryStatusFilter === 'all') return true;
                    const dStatus = item.deliveryStatus || (item.status === 'delivered' ? 'delivered' : 'pending');
                    return dStatus === deliveryStatusFilter;
                }
                return true;
            };
            return matchesSearch() && matchesStatus();
        });
    };

    const getCalculatedRating = (item, tab) => {
        if (tab === 'Foods' || tab === 'Featured') return { rating: item.rating || '—', count: item.reviews || '—' };
        
        const approvedReviews = reviews.filter(r => r.status === 'approved');
        let matchedReviews = [];
        
        if (tab === 'Category') {
            matchedReviews = approvedReviews.filter(r => r.category === item.title);
        } else if (tab === 'Market') {
            matchedReviews = approvedReviews.filter(r => r.market === item.title);
        }
        
        if (matchedReviews.length === 0) return { rating: '—', count: 0 };
        
        const sum = matchedReviews.reduce((acc, r) => acc + (r.rating || 0), 0);
        return { rating: (sum / matchedReviews.length).toFixed(1), count: matchedReviews.length };
    };

    const activeList = getFilteredItems();

    // ── Pagination logic ───────────────────────────────────────────────────────
    const getCurrentItems = (items) => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return items.slice(startIndex, endIndex);
    };

    const getTotalPages = (items) => {
        return Math.ceil(items.length / itemsPerPage);
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const goToPrevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const paginatedItems = getCurrentItems(activeList);
    const totalItems = activeList.length;
    const calculatedTotalPages = getTotalPages(activeList);

    useEffect(() => {
        setTotalPages(calculatedTotalPages);
        if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
            setCurrentPage(calculatedTotalPages);
        } else if (calculatedTotalPages === 0) {
            setCurrentPage(1);
        }
    }, [activeList, currentPage, calculatedTotalPages]);

    // ── CRUD handlers ─────────────────────────────────────────────────────────
    const handleSave = async (formData) => {
        setError('');
        try {
            if (activeTab === 'Foods') {
                formData.status = 'approved';
                if (editItem?.firestoreId) await updateFood(editItem.firestoreId, formData);
                else await addFood(formData);
                await loadFoods();
            } else if (activeTab === 'Market') {
                if (editItem?.firestoreId) await updateMarket(editItem.firestoreId, formData);
                else await addMarket(formData);
                await loadMarkets();
            } else if (activeTab === 'Featured') {
                const id = editItem?.firestoreId || formData.title.toLowerCase().replace(/\s+/g, '_');
                await setFeatured(id, formData);
                await loadFeatured();
            } else if (activeTab === 'Category') {
                if (editItem?.firestoreId) await updateCategory(editItem.firestoreId, formData);
                else await addCategory(formData);
                await loadCategories();
            }
            setFormOpen(false);
            setEditItem(null);
        } catch (e) {
            setError('Save failed: ' + e.message);
            throw e;
        }
    };

    const handleSaveCustomer = async (formData) => {
        setError('');
        try {
            if (editCustomer?.firestoreId) {
                await updateUser(editCustomer.firestoreId, formData);
                await loadCustomers();
            }
            setCustomerFormOpen(false);
            setEditCustomer(null);
        } catch (e) {
            setError('Failed to update customer: ' + e.message);
            throw e;
        }
    };

    const handleSaveOrder = async (formData) => {
        setError('');
        try {
            if (editOrder?.firestoreId) {
                await updateTransaction(editOrder.firestoreId, formData);
                await loadOrders();
                await loadPayments();
                await loadDelivery();
            }
            setOrderFormOpen(false);
            setEditOrder(null);
        } catch (e) {
            setError('Failed to update transaction/order: ' + e.message);
            throw e;
        }
    };

    const handleSaveCoBuy = async (formData) => {
        setError('');
        try {
            if (editCoBuyGroup?.firestoreId) {
                await updateCoBuyGroup(editCoBuyGroup.firestoreId, formData);
                await loadCoBuyGroups();
            }
            setCoBuyFormOpen(false);
            setEditCoBuyGroup(null);
        } catch (e) {
            setError('Failed to update co-buying pool: ' + e.message);
            throw e;
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Delete "${item.title}"?`)) return;
        setError('');
        try {
            if (activeTab === 'Foods') { await deleteFood(item.firestoreId); await loadFoods(); }
            else if (activeTab === 'Market') { await deleteMarket(item.firestoreId); await loadMarkets(); }
            else if (activeTab === 'Featured') { await deleteFeatured(item.firestoreId); await loadFeatured(); }
            else if (activeTab === 'Category') { await deleteCategory(item.firestoreId); await loadCategories(); }
        } catch (e) {
            setError('Delete failed: ' + e.message);
        }
    };

    const handleDeleteCustomer = async (item) => {
        if (!window.confirm(`Delete customer account for "${item.FirstName} ${item.LastName}"?`)) return;
        setError('');
        try {
            await deleteUser(item.firestoreId);
            await loadCustomers();
        } catch (e) {
            setError('Delete customer failed: ' + e.message);
        }
    };

    const handleDeleteOrder = async (item) => {
        if (!window.confirm(`Delete details with reference "${item.reference}"?`)) return;
        setError('');
        try {
            await deleteTransaction(item.firestoreId);
            await loadOrders();
            await loadPayments();
            await loadDelivery();
        } catch (e) {
            setError('Delete transaction failed: ' + e.message);
        }
    };

    const handleDeleteCoBuyGroup = async (item) => {
        if (!window.confirm(`Delete co-buying group "${item.name}"?`)) return;
        setError('');
        try {
            await deleteCoBuyGroup(item.firestoreId);
            await loadCoBuyGroups();
        } catch (e) {
            setError('Delete co-buying group failed: ' + e.message);
        }
    };

    const handleDeleteNewsletter = async (item) => {
        if (!window.confirm(`Delete newsletter subscription for "${item.email}"?`)) return;
        setError('');
        try {
            await deleteNewsletterSubscription(item.firestoreId);
            await loadNewsletterSubscriptions();
        } catch (e) {
            setError('Delete newsletter subscription failed: ' + e.message);
        }
    };

    const handleDeleteMessage = async (item) => {
        if (!window.confirm(`Delete contact message from "${item.name || item.email}"?`)) return;
        setError('');
        try {
            await deleteContactMessage(item.firestoreId);
            await loadContactMessages();
        } catch (e) {
            setError('Delete contact message failed: ' + e.message);
        }
    };

    const handleDeleteBlog = async (item) => {
        if (!window.confirm(`Delete blog post "${item.title}"?`)) return;
        setError('');
        try {
            await deleteBlogPost(item.firestoreId);
            await loadBlogPosts();
        } catch (e) {
            setError('Delete blog post failed: ' + e.message);
        }
    };

    const handleDeleteReview = async (item) => {
        if (!window.confirm(`Delete review by "${item.userName}"?`)) return;
        setError('');
        try {
            await deleteReview(item.firestoreId);
            await loadReviews();
        } catch (e) {
            setError('Delete review failed: ' + e.message);
        }
    };

    const handleApproveReview = async (item) => {
        if (!window.confirm(`Approve review by "${item.userName}"?`)) return;
        setError('');
        try {
            await updateReview(item.firestoreId, { status: 'approved' });
            await loadReviews();
        } catch (e) {
            setError('Approve review failed: ' + e.message);
        }
    };

    const handleApproveFood = async (item) => {
        if (!window.confirm(`Approve food "${item.title}"?`)) return;
        setError('');
        try {
            await updateFood(item.firestoreId, { status: 'approved' });
            await loadFoods();
        } catch (e) {
            setError('Approve food failed: ' + e.message);
        }
    };

    const handleRejectFood = async (item) => {
        if (!window.confirm(`Reject food "${item.title}"?`)) return;
        setError('');
        try {
            await updateFood(item.firestoreId, { status: 'rejected' });
            await loadFoods();
        } catch (e) {
            setError('Reject food failed: ' + e.message);
        }
    };

    const handleMarkPODPaid = async (item) => {
        if (!window.confirm(`Mark transaction "${item.reference}" as Pay-on-Delivery Collected (pay_on_delivery_success)?`)) return;
        setError('');
        try {
            await updateTransaction(item.firestoreId, { status: 'pay_on_delivery_success' });
            await loadPayments();
        } catch (e) {
            setError('Mark POD paid failed: ' + e.message);
        }
    };

    const handleSaveVat = async (e) => {
        e.preventDefault();
        const vatValue = parseFloat(vatInput);
        if (isNaN(vatValue) || vatValue < 0 || vatValue > 100) {
            setError('Please enter a valid VAT percentage (0–100).');
            return;
        }
        setError('');
        try {
            const existing = appSettings.find(s => s.key === 'vat_percentage');
            if (existing?.firestoreId) {
                await updateAppSetting(existing.firestoreId, { key: 'vat_percentage', value: vatValue });
            } else {
                await addAppSetting({ key: 'vat_percentage', value: vatValue });
            }
            await loadAppSettings();
            alert(`VAT updated to ${vatValue}%`);
        } catch (e) {
            setError('Save VAT failed: ' + e.message);
        }
    };

    const handleSaveBlog = async (event) => {
        event.preventDefault();
        setError('');
        try {
            if (editBlog?.firestoreId) {
                await updateBlogPost(editBlog.firestoreId, blogFormData);
            } else {
                await addBlogPost({ ...blogFormData, createdAt: new Date().toISOString() });
            }
            await loadBlogPosts();
            setBlogFormOpen(false);
            setEditBlog(null);
            setBlogFormData({ title: '', description: '', imageSrc: '', author: '', category: '', link: '', pricingText: '', locationText: '', rating: '' });
        } catch (e) {
            setError('Save blog post failed: ' + e.message);
            throw e;
        }
    };

    const handleSaveNews = async (event) => {
        event.preventDefault();
        setError('');
        try {
            const payload = {
                ...newsFormData,
                published: newsFormData.published !== false,
                updatedAt: new Date().toISOString()
            };
            if (editNews?.firestoreId) {
                await updateNews(editNews.firestoreId, payload);
            } else {
                await addNews({ ...payload, createdAt: new Date().toISOString() });
            }
            await loadNews();
            setNewsFormOpen(false);
            setEditNews(null);
            setNewsFormData({ title: '', excerpt: '', url: '', imageSrc: '', category: '', source: '', publishedAt: '', published: true });
        } catch (e) {
            setError('Save news failed: ' + e.message);
        }
    };

    const handleDeleteNewsArticle = async (item) => {
        if (!window.confirm(`Delete news article "${item.title}"?`)) return;
        setError('');
        try {
            await deleteNews(item.firestoreId);
            await loadNews();
        } catch (e) {
            setError('Delete news failed: ' + e.message);
        }
    };

    const handleApproveInterestAsNew = async (interestData) => {
        setError('');
        try {
            // Prepare initial member if auto-join is enabled
            const initialMembers = [];
            let initialAmount = 0;
            
            if (interestData.autoJoinSubmitter && interestData.submitterContribution) {
                const firstMember = {
                    userId: editInterest.submittedBy,
                    userName: editInterest.submittedByName,
                    userEmail: editInterest.submittedByEmail,
                    amount: Number(interestData.submitterContribution),
                    items: interestData.submitterPortion ? [{
                        title: interestData.submitterPortion,
                        quantity: 1,
                        price: Number(interestData.submitterContribution),
                        total: Number(interestData.submitterContribution)
                    }] : []
                };
                initialMembers.push(firstMember);
                initialAmount = Number(interestData.submitterContribution);
            }
            
            const newGroup = {
                name: interestData.name,
                state: interestData.state || "",
                neighborhood: interestData.neighborhood,
                bulkItem: interestData.bulkItem,
                groupType: interestData.groupType || "fixed",
                targetAmount: Number(interestData.targetAmount),
                maxMembers: Number(interestData.maxMembers),
                dropoffAddress: interestData.dropoffAddress,
                expiryDate: interestData.expiryDate,
                currentAmount: initialAmount,
                status: "active",
                createdBy: editInterest.submittedBy,
                createdByName: editInterest.submittedByName,
                createdAt: new Date().toISOString(),
                members: initialMembers,
                marketplaceProducts: interestData.marketplaceProducts || [],
                pendingItemRequests: [],
                messages: [
                    {
                        userId: "system",
                        userName: "System",
                        text: `Co-Op group "${interestData.name}" created for ${interestData.neighborhood}. Your interest has been approved!${initialMembers.length > 0 ? ` ${editInterest.submittedByName} joined as first member.` : ''}`,
                        timestamp: new Date().toISOString()
                    }
                ]
            };
            
            await createCoBuyGroup(newGroup);
            await updateCoBuyInterest(editInterest.firestoreId, { 
                status: 'approved', 
                approvedAt: new Date().toISOString(),
                approvedAsGroupId: 'new'
            });
            await loadCoBuyGroups();
            await loadCoBuyInterests();
            setInterestFormOpen(false);
            setEditInterest(null);
        } catch (e) {
            setError('Approve interest failed: ' + e.message);
            throw e;
        }
    };

    const handleAddInterestToExisting = async (groupId, memberData) => {
        setError('');
        try {
            // If admin wants to add submitter as member
            if (memberData && memberData.addAsMember && memberData.contribution) {
                // Fetch the group first
                const group = coBuyGroups.find(g => g.firestoreId === groupId);
                if (!group) {
                    throw new Error('Group not found');
                }
                
                // Check if user is already a member
                if (group.members && group.members.some(m => m.userId === editInterest.submittedBy)) {
                    throw new Error('User is already a member of this group');
                }
                
                // Create new member
                const newMember = {
                    userId: editInterest.submittedBy,
                    userName: editInterest.submittedByName,
                    userEmail: editInterest.submittedByEmail,
                    amount: Number(memberData.contribution),
                    items: memberData.portion ? [{
                        title: memberData.portion,
                        quantity: 1,
                        price: Number(memberData.contribution),
                        total: Number(memberData.contribution)
                    }] : []
                };
                
                // Update group with new member
                const updatedMembers = [...(group.members || []), newMember];
                const newTotal = Number(group.currentAmount || 0) + Number(memberData.contribution);
                const newStatus = newTotal >= group.targetAmount ? "completed" : group.status;
                
                // Add system message
                const newMessage = {
                    userId: "system",
                    userName: "System",
                    text: `${editInterest.submittedByName} joined the group from approved interest.`,
                    timestamp: new Date().toISOString()
                };
                const updatedMessages = [...(group.messages || []), newMessage];
                
                await updateCoBuyGroup(groupId, {
                    members: updatedMembers,
                    currentAmount: newTotal,
                    status: newStatus,
                    messages: updatedMessages
                });
            }
            
            // Update interest status
            await updateCoBuyInterest(editInterest.firestoreId, { 
                status: 'approved', 
                approvedAt: new Date().toISOString(),
                approvedAsGroupId: groupId
            });
            
            await loadCoBuyGroups();
            await loadCoBuyInterests();
            setInterestFormOpen(false);
            setEditInterest(null);
        } catch (e) {
            setError('Add to existing group failed: ' + e.message);
            throw e;
        }
    };

    const handleRejectInterest = async () => {
        setError('');
        try {
            await updateCoBuyInterest(editInterest.firestoreId, { 
                status: 'rejected', 
                rejectedAt: new Date().toISOString()
            });
            await loadCoBuyInterests();
            setInterestFormOpen(false);
            setEditInterest(null);
        } catch (e) {
            setError('Reject interest failed: ' + e.message);
            throw e;
        }
    };

    // Merge co-buy products by productId, summing quantities for duplicates
    const mergeCoBuyProducts = (existing = [], incoming = []) => {
        const map = new Map();
        [...(existing || []), ...(incoming || [])].forEach(p => {
            if (!p) return;
            const id = p.productId;
            if (map.has(id)) {
                const cur = map.get(id);
                map.set(id, { ...cur, quantity: (Number(cur.quantity) || 1) + (Number(p.quantity) || 1) });
            } else {
                map.set(id, { ...p });
            }
        });
        return Array.from(map.values());
    };

    // Approve a fixed-group member's item request → merge into marketplaceProducts
    const handleApproveItemRequest = async (group, requestIndex) => {
        setError('');
        try {
            const requests = (group.pendingItemRequests || []).map((r, i) =>
                i === requestIndex ? { ...r, status: 'approved', approvedAt: new Date().toISOString() } : r
            );
            const approved = (group.pendingItemRequests || [])[requestIndex];
            const merged = mergeCoBuyProducts(group.marketplaceProducts, approved?.items || []);
            await updateCoBuyGroup(group.firestoreId, {
                pendingItemRequests: requests,
                marketplaceProducts: merged
            });
            await loadCoBuyGroups();
            cogoToast.success("Item request approved and added to the group.");
        } catch (e) {
            setError('Approve request failed: ' + e.message);
        }
    };

    const handleRejectItemRequest = async (group, requestIndex) => {
        setError('');
        try {
            const requests = (group.pendingItemRequests || []).map((r, i) =>
                i === requestIndex ? { ...r, status: 'rejected', rejectedAt: new Date().toISOString() } : r
            );
            await updateCoBuyGroup(group.firestoreId, { pendingItemRequests: requests });
            await loadCoBuyGroups();
            cogoToast.success("Item request rejected.");
        } catch (e) {
            setError('Reject request failed: ' + e.message);
        }
    };

    const openInterestReview = (interest) => {
        setEditInterest(interest);
        setInterestFormOpen(true);
    };

    const handleCreateCoBuyGroup = async (data) => {
        setError('');
        try {
            await createCoBuyGroup(data);
            await loadCoBuyGroups();
            setCoBuyCreateOpen(false);
        } catch (e) {
            setError('Create co-buying group failed: ' + e.message);
            throw e;
        }
    };

    const openAdd = () => { 
        if (activeTab === 'Co-Buying') {
            setCoBuyCreateOpen(true);
        } else if (activeTab === 'Blog') {
            setEditBlog(null);
            setBlogFormData({ title: '', description: '', imageSrc: '', author: '', category: '', link: '', pricingText: '', locationText: '', rating: '' });
            setBlogFormOpen(true);
        } else {
            setEditItem(null); 
            setFormOpen(true); 
        }
    };

    const openEdit = (item) => {
        if (activeTab === 'Customers') {
            setEditCustomer(item);
            setCustomerFormOpen(true);
        } else if (activeTab === 'Orders' || activeTab === 'Payments' || activeTab === 'Delivery') {
            const matchingCustomer = customers.find(c => c.uid === item.userId || c.Email === item.userEmail);
            const enrichedOrder = {
                ...item,
                customerAddress: matchingCustomer ? (matchingCustomer.address || matchingCustomer.deliveryAddress) : 'No address set in profile'
            };
            setEditOrder(enrichedOrder);
            setOrderFormOpen(true);
        } else if (activeTab === 'Co-Buying') {
            setEditCoBuyGroup(item);
            setCoBuyFormOpen(true);
        } else if (activeTab === 'Blog') {
            setEditBlog(item);
            setBlogFormData({
                title: item.title || '',
                description: item.description || '',
                imageSrc: item.imageSrc || '',
                author: item.author || '',
                category: item.category || '',
                link: item.link || '',
                pricingText: item.pricingText || '',
                locationText: item.locationText || '',
                rating: item.rating || ''
            });
            setBlogFormOpen(true);
        } else {
            setEditItem(item);
            setFormOpen(true);
        }
    };

    const handleSignOut = () => auth.signOut().then(() => history.replace('/login'));

    // Only the super admin can change a user's role. Roles are restricted to
    // sub-admin or regular user — super admin is reserved for SUPER_ADMIN_EMAIL.
    const handleChangeRole = async (customer, newRole) => {
        if (customer.Email === SUPER_ADMIN_EMAIL) return;
        if (!window.confirm(`Change role of ${customer.FirstName} ${customer.LastName} to "${newRole}"?`)) return;
        setError('');
        try {
            await updateUser(customer.firestoreId, { role: newRole });
            await loadCustomers();
            cogoToast.success(`Role updated to ${newRole}.`);
        } catch (e) {
            setError('Failed to update role: ' + e.message);
        }
    };

    if (!authChecked) return <Spinner>Checking authentication...</Spinner>;
    if (!user) return null;

    return (
        <Page>
            <Sidebar>
                <SidebarHeader>
                    <Logo
                        as="button"
                        onClick={() => history.push('/')}
                        style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0, color: 'white' }}
                    >
                        <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 24, height: 24 }}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m0-12.728l.707.707m12.728 12.728l.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
                        </svg>
                        {APP_NAME}
                    </Logo>
                </SidebarHeader>

                <SidebarNav>
                    {TABS.map(tab => (
                        <SidebarLink key={tab} active={activeTab === tab} onClick={() => {
                            setActiveTab(tab);
                            setCurrentPage(1);
                            setSearchTerm('');
                            setStatusFilter('all');
                            setDeliveryStatusFilter('all');
                        }}>
                            {TabIcons[tab] || null}
                            {tab === 'Overview' ? 'Dashboard' : tab}
                        </SidebarLink>
                    ))}
                </SidebarNav>

                <SidebarFooter>
                    <UserInfo>
                        <span style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 600 }}>Logged in as</span>
                        <UserEmail>{user.email}</UserEmail>
                    </UserInfo>
                    <SignOutButton onClick={handleSignOut}>Sign Out</SignOutButton>
                </SidebarFooter>
            </Sidebar>

            <MainContent>
                <ContentHeader>
                    <div>
                        <PageTitle>
                            {activeTab === 'Overview' ? 'Analytics Dashboard' : activeTab}
                        </PageTitle>
                        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                            Admin Portal Hub
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                        <button
                            type="button"
                            onClick={() => openClearModal(activeTab === 'Foods' ? 'foods' : activeTab === 'Category' ? 'categories' : activeTab === 'Locations' ? 'locations' : activeTab === 'Market' ? 'markets' : activeTab === 'Featured' ? 'featured' : null)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '8px 14px',
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                color: '#b91c1c',
                                borderRadius: '8px',
                                fontSize: '0.82rem',
                                fontWeight: 700,
                                cursor: 'pointer',
                                transition: 'all 0.15s ease'
                            }}
                            title="Clear demo or existing records from Firestore"
                        >
                            <span role="img" aria-label="broom">🧹</span>
                            <span>Clear / Reset Data...</span>
                        </button>
                    </div>
                </ContentHeader>

                {error && <ErrorMsg>{error}</ErrorMsg>}

                {/* ── Tab: Overview ────────────────────────────────────────────── */}
                {activeTab === 'Overview' && (
                    <>
                        <MetricsGrid>
                            <MetricCard>
                                <div>
                                    <MetricLabel>Total Revenue</MetricLabel>
                                    <MetricValue>₦{metrics.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</MetricValue>
                                </div>
                                <MetricIconContainer color="green">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 24, height: 24 }}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </MetricIconContainer>
                            </MetricCard>

                            <MetricCard>
                                <div>
                                    <MetricLabel>Total Orders</MetricLabel>
                                    <MetricValue>{metrics.totalOrders}</MetricValue>
                                </div>
                                <MetricIconContainer color="blue">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 24, height: 24 }}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                </MetricIconContainer>
                            </MetricCard>

                            <MetricCard>
                                <div>
                                    <MetricLabel>Active Deliveries</MetricLabel>
                                    <MetricValue>{metrics.pendingDeliveries}</MetricValue>
                                </div>
                                <MetricIconContainer color="amber">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 24, height: 24 }}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0zM13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1" />
                                    </svg>
                                </MetricIconContainer>
                            </MetricCard>

                            <MetricCard>
                                <div>
                                    <MetricLabel>Total Customers</MetricLabel>
                                    <MetricValue>{metrics.activeCustomers}</MetricValue>
                                </div>
                                <MetricIconContainer color="indigo">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 24, height: 24 }}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                </MetricIconContainer>
                            </MetricCard>
                        </MetricsGrid>

                        <ChartsGrid>
                            <ChartCard style={{ gridColumn: 'span 2' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                    <ChartTitle>
                                        {revenueRange === 'day' && '7-Day Revenue Trend (₦)'}
                                        {revenueRange === 'month' && '30-Day Revenue Trend (₦)'}
                                        {revenueRange === '3months' && '3-Month Revenue Trend (₦)'}
                                    </ChartTitle>
                                    <div style={{ display: 'flex', gap: 4, background: '#f3f4f6', padding: 3, borderRadius: 8 }}>
                                        {['day', 'month', '3months'].map((r) => (
                                            <button
                                                key={r}
                                                onClick={() => setRevenueRange(r)}
                                                style={{
                                                    padding: '4px 12px',
                                                    borderRadius: 6,
                                                    border: 'none',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    textTransform: 'capitalize',
                                                    background: revenueRange === r ? '#10b981' : 'transparent',
                                                    color: revenueRange === r ? '#fff' : '#6b7280',
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                {r === '3months' ? '3 Months' : r}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div style={{ height: 180, display: 'flex', alignItems: 'center' }}>
                                    <RevenueLineChart data={revenueTrend} labelStep={revenueLabelStep} />
                                </div>
                            </ChartCard>

                            <ChartCard>
                                <ChartTitle>Delivery Performance</ChartTitle>
                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <DeliveryProgressRing counts={deliveryStatuses} />
                                </div>
                            </ChartCard>
                        </ChartsGrid>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', flexWrap: 'wrap' }}>
                            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '1rem', color: '#374151' }}>Order Volume breakdown</div>
                                <div style={{ height: 180 }}>
                                    <OrderStatusBarChart counts={orderStatuses} />
                                </div>
                            </div>

                            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                <div style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '1rem', color: '#374151' }}>Recent Activities</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: 180, overflowY: 'auto' }}>
                                    {orders.slice(0, 4).map((ord, i) => (
                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6', paddingBottom: 6, fontSize: '0.85rem' }}>
                                            <div>
                                                <span style={{ fontWeight: 600, color: '#374151' }}>{ord.userName || ord.userEmail}</span>
                                                <span style={{ color: '#9ca3af', marginLeft: 8 }}>{ord.reference}</span>
                                            </div>
                                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                <span style={{ fontWeight: 600, color: '#10b981' }}>₦{ord.total?.toFixed(2)}</span>
                                                <Badge status={ord.status}>{ord.status}</Badge>
                                            </div>
                                        </div>
                                    ))}
                                    {orders.length === 0 && <div style={{ fontStyle: 'italic', color: '#9ca3af', textAlign: 'center', marginTop: '2rem' }}>No recent transaction activity</div>}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* ── Filters & Search Row ────────────────────────────────────── */}
                {activeTab !== 'Overview' && activeTab !== 'Locations' && (
                    <FilterRow>
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            <SearchInput
                                type="text"
                                placeholder={`Search in ${activeTab.toLowerCase()}...`}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />

                            {(activeTab === 'Orders' || activeTab === 'Payments') && (
                                <SelectFilter value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                    <option value="all">All Payment Statuses</option>
                                    <option value="success">Paid / Success</option>
                                    <option value="pending">Pending</option>
                                    <option value="failed">Failed</option>
                                    <option value="refunded">Refunded</option>
                                    <option value="cancelled">Cancelled</option>
                                </SelectFilter>
                            )}

                            {activeTab === 'Delivery' && (
                                <SelectFilter value={deliveryStatusFilter} onChange={e => setDeliveryStatusFilter(e.target.value)}>
                                    <option value="all">All Delivery Statuses</option>
                                    <option value="pending">Pending</option>
                                    <option value="dispatching">Dispatching</option>
                                    <option value="in_transit">In Transit</option>
                                    <option value="delivered">Delivered</option>
                                    <option value="failed">Failed</option>
                                </SelectFilter>
                            )}
                        </div>

                        <div>
                            <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                                Showing {totalItems} items
                            </span>
                        </div>
                    </FilterRow>
                )}

                {/* ── Product Instructions and CRUD triggers ─────────────────── */}
                {activeTab !== 'Overview' && activeTab !== 'Locations' && (
                    <SectionHeader>
                        <SectionTitle>
                            {activeTab} Table View
                        </SectionTitle>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {['Foods', 'Category', 'Market', 'Featured', 'Orders', 'Co-Buying', 'Reviews', 'News', 'Blog'].includes(activeTab) && (
                                <button
                                    type="button"
                                    onClick={() => openClearModal(
                                        activeTab === 'Foods' ? 'foods' :
                                        activeTab === 'Category' ? 'categories' :
                                        activeTab === 'Market' ? 'markets' :
                                        activeTab === 'Featured' ? 'featured' :
                                        activeTab === 'Orders' ? 'orders' :
                                        activeTab === 'Co-Buying' ? 'co_buy_groups' :
                                        activeTab === 'Reviews' ? 'reviews' :
                                        activeTab === 'News' ? 'news' : 'blog'
                                    )}
                                    style={{
                                        padding: '8px 14px',
                                        background: '#fff',
                                        border: '1px solid #fca5a5',
                                        color: '#dc2626',
                                        borderRadius: '8px',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        cursor: 'pointer'
                                    }}
                                    title={`Clear all ${activeTab} data...`}
                                >
                                    Clear {activeTab}...
                                </button>
                            )}
                            {activeTab !== 'Orders' && activeTab !== 'Customers' && activeTab !== 'Payments' && activeTab !== 'Delivery' && activeTab !== 'Partners' && activeTab !== 'Market' && (
                                <AddBtn onClick={openAdd}>
                                    {activeTab === 'Co-Buying' ? '+ Start New Co-Op Group' : activeTab === 'Blog' ? '+ Add Blog' : '+ Add Item'}
                                </AddBtn>
                            )}
                        </div>
                    </SectionHeader>
                )}

                {activeTab === 'Featured' && (
                    <FeaturedNote>
                        Featured items populate the homepage "From Our Stocks" slider.
                    </FeaturedNote>
                )}
                {activeTab === 'Category' && (
                    <FeaturedNote>
                        Ensure comma-separated list of items match exact names of Food/Market items.
                    </FeaturedNote>
                )}

                {/* ── Table Rendering ────────────────────────────────────────── */}
                {activeTab !== 'Overview' && activeTab !== 'Locations' && (
                    loading ? (
                        <Spinner>Loading {activeTab.toLowerCase()} records...</Spinner>
                    ) : (
                        <>
                            <Table>
                                <thead>
                                    <tr>
                                        {activeTab === 'Customers' && (
                                            <>
                                                <Th>ID</Th>
                                                <Th>Name</Th>
                                                <Th>Email</Th>
                                                <Th>Phone</Th>
                                                <Th>Registered Address</Th>
                                                <Th>Joined Date</Th>
                                                <Th>Role</Th>
                                                <Th>Actions</Th>
                                            </>
                                        )}
                                        {activeTab === 'Orders' && (
                                            <>
                                                <Th>Order ID</Th>
                                                <Th>Customer</Th>
                                                <Th>Order Date</Th>
                                                <Th>Payment Status</Th>
                                                <Th>Delivery Status</Th>
                                                <Th>Total Amount</Th>
                                                <Th>Actions</Th>
                                            </>
                                        )}
                                        {activeTab === 'Payments' && (
                                            <>
                                                <Th>Transaction ID</Th>
                                                <Th>Customer</Th>
                                                <Th>Date</Th>
                                                <Th>Amount</Th>
                                                <Th>Status</Th>
                                                <Th>Payment Method</Th>
                                                <Th>Actions</Th>
                                            </>
                                        )}
                                        {activeTab === 'Delivery' && (
                                            <>
                                                <Th>Order Ref</Th>
                                                <Th>Customer</Th>
                                                <Th>Address</Th>
                                                <Th>Expected Delivery</Th>
                                                <Th>Delivery Status</Th>
                                                <Th>Assigned Driver</Th>
                                                <Th>Actions</Th>
                                            </>
                                        )}
                                        {activeTab === 'Co-Buying' && (
                                            <>
                                                <Th>Group Name</Th>
                                                <Th>Type</Th>
                                                <Th>Neighborhood</Th>
                                                <Th>Bulk Item</Th>
                                                <Th>Funding Status</Th>
                                                <Th>Members</Th>
                                                <Th>Group Status</Th>
                                                <Th>Expiry Date</Th>
                                                <Th>Actions</Th>
                                            </>
                                        )}
                                        {activeTab === 'Partners' && (
                                            <>
                                                <Th>Business Name</Th>
                                                <Th>Partner Type</Th>
                                                <Th>Contact</Th>
                                                <Th>Service Areas</Th>
                                                <Th>Status</Th>
                                                <Th>Submitted</Th>
                                                <Th>Actions</Th>
                                            </>
                                        )}
                                        {(activeTab === 'Foods' || activeTab === 'Market' || activeTab === 'Featured' || activeTab === 'Category') && (
                                            <>
                                                <Th>Image</Th>
                                                <Th>Title</Th>
                                                <Th>{activeTab === 'Market' ? 'Items' : activeTab === 'Category' ? 'Items' : 'Category'}</Th>
                                                <Th>Price (₦)</Th>
                                                <Th>Rating</Th>
                                                <Th>Reviews</Th>
                                                {activeTab === 'Foods' && <Th>Status</Th>}
                                                <Th>Actions</Th>
                                            </>
                                        )}
                                        {activeTab === 'Newsletter' && (
                                            <>
                                                <Th>Email</Th>
                                                <Th>Subscribed At</Th>
                                                <Th>Actions</Th>
                                            </>
                                        )}
                                        {activeTab === 'Messages' && (
                                            <>
                                                <Th>Name</Th>
                                                <Th>Email</Th>
                                                <Th>Message</Th>
                                                <Th>Created At</Th>
                                                <Th>Actions</Th>
                                            </>
                                        )}
                                        {activeTab === 'Blog' && (
                                            <>
                                                <Th>Image</Th>
                                                <Th>Title</Th>
                                                <Th>Category</Th>
                                                <Th>Author</Th>
                                                <Th>Link</Th>
                                                <Th>Actions</Th>
                                            </>
                                        )}
                                        {activeTab === 'Reviews' && (
                                            <>
                                                <Th>User</Th>
                                                <Th>Item</Th>
                                                <Th>Rating</Th>
                                                <Th>Comment</Th>
                                                <Th>Status</Th>
                                                <Th>Date</Th>
                                                <Th>Actions</Th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedItems.length === 0 ? (
                                        <EmptyRow>
                                            <EmptyTd colSpan={8}>
                                                No matches found for {activeTab.toLowerCase()}
                                            </EmptyTd>
                                        </EmptyRow>
                                    ) : (
                                        paginatedItems.map((item) => (
                                            <tr key={item.firestoreId || item.id || item.reference}>
                                                {activeTab === 'Customers' && (
                                                    <>
                                                        <Td>{item.firestoreId}</Td>
                                                        <Td style={{ fontWeight: 600 }}>{item.FirstName} {item.LastName}</Td>
                                                        <Td>{item.Email}</Td>
                                                        <Td>{item.Phone || '—'}</Td>
                                                        <Td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {item.address || item.deliveryAddress || '—'}
                                                        </Td>
                                                        <Td>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—'}</Td>
                                                        <Td>
                                                            {item.Email === SUPER_ADMIN_EMAIL ? (
                                                                <Badge status="success">Super Admin</Badge>
                                                            ) : isSuperAdmin ? (
                                                                <RoleSelect
                                                                    value={item.role || ROLE_USER}
                                                                    onChange={(e) => handleChangeRole(item, e.target.value)}
                                                                >
                                                                    <option value={ROLE_USER}>User</option>
                                                                    <option value={ROLE_SUB_ADMIN}>Sub-Admin</option>
                                                                </RoleSelect>
                                                            ) : (
                                                                <Badge status={item.role === ROLE_SUB_ADMIN ? 'pending' : 'success'}>
                                                                    {item.role === ROLE_SUB_ADMIN ? 'Sub-Admin' : 'User'}
                                                                </Badge>
                                                            )}
                                                        </Td>
                                                        <Td>
                                                            <EditBtn onClick={() => openEdit(item)}>Edit</EditBtn>
                                                            <DeleteBtn onClick={() => handleDeleteCustomer(item)}>Delete</DeleteBtn>
                                                        </Td>
                                                    </>
                                                )}
                                                {activeTab === 'Orders' && (
                                                    <>
                                                        <Td style={{ fontFamily: 'monospace' }}>{item.reference}</Td>
                                                        <Td style={{ fontWeight: 600 }}>{item.userName || item.userEmail}</Td>
                                                        <Td>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—'}</Td>
                                                        <Td><Badge status={item.status}>{item.status}</Badge></Td>
                                                        <Td>
                                                            <Badge status={item.deliveryStatus || (item.status === 'delivered' ? 'delivered' : 'pending')}>
                                                                {item.deliveryStatus || (item.status === 'delivered' ? 'delivered' : 'pending')}
                                                            </Badge>
                                                        </Td>
                                                        <Td style={{ fontWeight: 600 }}>₦{item.total?.toFixed(2)}</Td>
                                                        <Td>
                                                            <EditBtn onClick={() => openEdit(item)}>Manage</EditBtn>
                                                            <DeleteBtn onClick={() => handleDeleteOrder(item)}>Delete</DeleteBtn>
                                                        </Td>
                                                    </>
                                                )}
                                                {activeTab === 'Payments' && (
                                                    <>
                                                        <Td style={{ fontFamily: 'monospace' }}>{item.reference}</Td>
                                                        <Td style={{ fontWeight: 600 }}>{item.userName || item.userEmail}</Td>
                                                        <Td>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—'}</Td>
                                                        <Td style={{ fontWeight: 600 }}>₦{item.total?.toFixed(2)}</Td>
                                                        <Td><Badge status={item.status}>{item.status}</Badge></Td>
                                                        <Td>{item.paymentMethod === 'pay_on_delivery' ? '💵 Pay on Delivery' : '💳 Paystack'}</Td>
                                                        <Td>
                                                            {item.status === 'pay_on_delivery_pending' && (
                                                                <EditBtn onClick={() => handleMarkPODPaid(item)} style={{ marginRight: 8 }}>✅ Mark Collected</EditBtn>
                                                            )}
                                                            <EditBtn onClick={() => openEdit(item)}>View</EditBtn>
                                                        </Td>
                                                    </>
                                                )}
                                                {activeTab === 'Delivery' && (
                                                    <>
                                                        <Td style={{ fontFamily: 'monospace' }}>{item.reference}</Td>
                                                        <Td style={{ fontWeight: 600 }}>{item.userName || item.userEmail}</Td>
                                                        <Td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {item.deliveryAddress || '—'}
                                                        </Td>
                                                        <Td>{item.expectedDeliveryDate ? new Date(item.expectedDeliveryDate).toLocaleDateString() : '—'}</Td>
                                                        <Td>
                                                            <Badge status={item.deliveryStatus || (item.status === 'delivered' ? 'delivered' : 'pending')}>
                                                                {item.deliveryStatus || (item.status === 'delivered' ? 'delivered' : 'pending')}
                                                            </Badge>
                                                        </Td>
                                                        <Td style={{ fontStyle: item.driver ? 'normal' : 'italic', color: item.driver ? 'inherit' : '#9ca3af' }}>
                                                            {item.driver || 'Not Assigned'}
                                                        </Td>
                                                        <Td>
                                                            <EditBtn onClick={() => openEdit(item)}>Manage Delivery</EditBtn>
                                                        </Td>
                                                    </>
                                                )}
                                                {activeTab === 'Co-Buying' && (
                                                    <>
                                                        <Td style={{ fontWeight: 600 }}>{item.name}</Td>
                                                        <Td>
                                                            <Badge status={item.groupType === 'dynamic' ? 'active' : 'pending'}>
                                                                {item.groupType === 'dynamic' ? 'Dynamic' : 'Fixed'}
                                                            </Badge>
                                                        </Td>
                                                        <Td>{item.neighborhood} Proximity</Td>
                                                        <Td style={{ fontWeight: 600, color: '#10b981' }}>{item.bulkItem}</Td>
                                                        <Td style={{ fontWeight: 600 }}>₦{(item.currentAmount || 0).toLocaleString()} / ₦{item.targetAmount?.toLocaleString()}</Td>
                                                        <Td>{item.members?.length || 0} joined</Td>
                                                        <Td><Badge status={item.status}>{item.status}</Badge></Td>
                                                        <Td>
                                                            {item.expiryDate ? (
                                                                <span style={{ 
                                                                    fontSize: '0.75rem', 
                                                                    color: new Date(item.expiryDate) < new Date() ? '#dc2626' : '#6b7280' 
                                                                }}>
                                                                    {new Date(item.expiryDate).toLocaleString()}
                                                                    {new Date(item.expiryDate) < new Date() && ' (Expired)'}
                                                                </span>
                                                            ) : '—'}
                                                        </Td>
                                                        <Td>
                                                            <EditBtn onClick={() => openEdit(item)}>Manage Co-Op</EditBtn>
                                                            <DeleteBtn onClick={() => handleDeleteCoBuyGroup(item)}>Delete</DeleteBtn>
                                                        </Td>
                                                    </>
                                                )}
                                                {activeTab === 'Partners' && (
                                                    <>
                                                        <Td style={{ fontWeight: 600 }}>{item.businessName}</Td>
                                                        <Td style={{ textTransform: 'capitalize' }}>{item.partnerType}</Td>
                                                        <Td>
                                                            <div style={{ fontSize: '0.8rem' }}>{item.name}</div>
                                                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{item.email}</div>
                                                        </Td>
                                                        <Td style={{ fontSize: '0.75rem' }}>{(item.serviceAreas || []).join(', ') || '—'}</Td>
                                                        <Td>
                                                            <Badge status={item.verificationStatus === 'approved' ? 'success' : item.verificationStatus === 'rejected' ? 'failed' : 'pending'}>
                                                                {item.verificationStatus}
                                                            </Badge>
                                                        </Td>
                                                        <Td>{item.submittedAt ? new Date(item.submittedAt).toLocaleDateString() : '—'}</Td>
                                                        <Td>
                                                            {item.verificationStatus === 'pending' && (
                                                                <>
                                                                    <EditBtn onClick={async () => {
                                                                        if (!window.confirm(`Approve ${item.businessName}?`)) return;
                                                                        await updatePartner(item.firestoreId, { verificationStatus: 'approved', approvedAt: new Date().toISOString() });
                                                                        loadPartners();
                                                                    }}>Approve</EditBtn>
                                                                    <DeleteBtn onClick={async () => {
                                                                        if (!window.confirm(`Reject ${item.businessName}?`)) return;
                                                                        await updatePartner(item.firestoreId, { verificationStatus: 'rejected', rejectedAt: new Date().toISOString() });
                                                                        loadPartners();
                                                                    }}>Reject</DeleteBtn>
                                                                </>
                                                            )}
                                                            {item.verificationStatus !== 'pending' && (
                                                                <DeleteBtn onClick={async () => {
                                                                    if (!window.confirm(`Delete partner ${item.businessName}?`)) return;
                                                                    await deletePartner(item.firestoreId);
                                                                    loadPartners();
                                                                }}>Delete</DeleteBtn>
                                                            )}
                                                        </Td>
                                                    </>
                                                )}
                                                {(activeTab === 'Foods' || activeTab === 'Market' || activeTab === 'Featured' || activeTab === 'Category') && (
                                                    <>
                                                        <Td>
                                                            {item.imageSrc
                                                                ? <ThumbImg src={item.imageSrc} alt={item.title} onError={e => { e.target.style.display = 'none'; }} />
                                                                : <span style={{ color: '#ccc', fontSize: '0.75rem' }}>No image</span>
                                                            }
                                                        </Td>
                                                        <Td style={{ fontWeight: 600 }}>{item.title}</Td>
                                                        <Td>{(activeTab === 'Market' || activeTab === 'Category')
                                                            ? (Array.isArray(item.contentArray) ? item.contentArray.join(', ') : item.content || '—')
                                                            : (item.content || '—')}
                                                        </Td>
                                                        <Td>{item.price ? `₦${item.price}` : '—'}</Td>
                                                        {(() => {
                                                            const { rating, count } = getCalculatedRating(item, activeTab);
                                                            return (
                                                                <>
                                                                    <Td>{rating}</Td>
                                                                    <Td>{count}</Td>
                                                                </>
                                                            );
                                                        })()}
                                                        {activeTab === 'Foods' && (
                                                            <Td>
                                                                <Badge status={item.status === 'approved' ? 'success' : item.status === 'rejected' ? 'failed' : 'pending'}>
                                                                    {item.status || 'approved'}
                                                                </Badge>
                                                            </Td>
                                                        )}
                                                        <Td>
                                                            {activeTab === 'Foods' && item.status === 'pending' && (
                                                                <>
                                                                    <EditBtn onClick={() => handleApproveFood(item)} style={{ marginRight: 8 }}>Approve</EditBtn>
                                                                    <DeleteBtn onClick={() => handleRejectFood(item)} style={{ marginRight: 8 }}>Reject</DeleteBtn>
                                                                </>
                                                            )}
                                                            <EditBtn onClick={() => openEdit(item)}>Edit</EditBtn>
                                                            <DeleteBtn onClick={() => handleDelete(item)}>Delete</DeleteBtn>
                                                        </Td>
                                                    </>
                                                )}
                                                {activeTab === 'Newsletter' && (
                                                    <>
                                                        <Td>{item.email || '—'}</Td>
                                                        <Td>{item.subscribedAt ? new Date(item.subscribedAt).toLocaleString() : '—'}</Td>
                                                        <Td>
                                                            <DeleteBtn onClick={() => handleDeleteNewsletter(item)}>Delete</DeleteBtn>
                                                        </Td>
                                                    </>
                                                )}
                                                {activeTab === 'Messages' && (
                                                    <>
                                                        <Td style={{ fontWeight: 600 }}>{item.name || '—'}</Td>
                                                        <Td>{item.email || '—'}</Td>
                                                        <Td style={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>{item.message || '—'}</Td>
                                                        <Td>{item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}</Td>
                                                        <Td>
                                                            <DeleteBtn onClick={() => handleDeleteMessage(item)}>Delete</DeleteBtn>
                                                        </Td>
                                                    </>
                                                )}
                                                {activeTab === 'Blog' && (
                                                    <>
                                                        <Td>
                                                            {item.imageSrc ? <ThumbImg src={item.imageSrc} alt={item.title} onError={e => { e.target.style.display = 'none'; }} /> : <span style={{ color: '#ccc', fontSize: '0.75rem' }}>No image</span>}
                                                        </Td>
                                                        <Td style={{ fontWeight: 600 }}>{item.title || '—'}</Td>
                                                        <Td>{item.category || '—'}</Td>
                                                        <Td>{item.author || '—'}</Td>
                                                        <Td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.link || '—'}</Td>
                                                        <Td>
                                                            <EditBtn onClick={() => openEdit(item)}>Edit</EditBtn>
                                                            <DeleteBtn onClick={() => handleDeleteBlog(item)}>Delete</DeleteBtn>
                                                        </Td>
                                                    </>
                                                )}
                                                {activeTab === 'Reviews' && (
                                                    <>
                                                        <Td style={{ fontWeight: 600 }}>{item.userName || '—'}</Td>
                                                        <Td>{item.foodName || '—'}</Td>
                                                        <Td>
                                                            {item.rating ? (
                                                                <span style={{ color: '#f59e0b' }}>
                                                                    {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}
                                                                </span>
                                                            ) : '—'}
                                                        </Td>
                                                        <Td style={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>{item.comment || '—'}</Td>
                                                        <Td>
                                                            <Badge status={item.status === 'approved' ? 'success' : item.status === 'rejected' ? 'failed' : 'pending'}>
                                                                {item.status || 'pending'}
                                                            </Badge>
                                                        </Td>
                                                        <Td>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—'}</Td>
                                                        <Td>
                                                            {item.status === 'pending' && (
                                                                <EditBtn onClick={() => handleApproveReview(item)}>Approve</EditBtn>
                                                            )}
                                                            <DeleteBtn onClick={() => handleDeleteReview(item)}>Delete</DeleteBtn>
                                                        </Td>
                                                    </>
                                                )}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>

                            {paginatedItems.length > 0 && (
                                <PaginationContainer>
                                    <PaginationButton
                                        onClick={goToPrevPage}
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </PaginationButton>

                                    <PageInfo>
                                        Page {currentPage} of {totalPages} ({totalItems} total {activeTab.toLowerCase()})
                                    </PageInfo>

                                    <PaginationButton
                                        onClick={goToNextPage}
                                        disabled={currentPage === totalPages || totalPages === 0}
                                    >
                                        Next
                                    </PaginationButton>
                                </PaginationContainer>
                            )}
                        </>
                    )
                )}

                {/* ── Served Locations (States & Neighborhoods) ──────────────── */}
                {activeTab === 'Locations' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                Manage covered delivery states and neighborhoods.
                            </div>
                            <button
                                type="button"
                                onClick={() => openClearModal('locations')}
                                style={{
                                    padding: '8px 14px',
                                    background: '#fff',
                                    border: '1px solid #fca5a5',
                                    color: '#dc2626',
                                    borderRadius: '8px',
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6
                                }}
                                title="Clear all served locations"
                            >
                                <span role="img" aria-label="wastebasket">🗑️</span>
                                <span>Clear Locations...</span>
                            </button>
                        </div>
                        <ServedAreasManager
                            states={servedStates}
                            loading={loading}
                            reload={loadServedStates}
                        />
                    </div>
                )}

                {/* ── Pending Interests Section (Co-Buying Tab Only) ────────── */}
                {activeTab === 'Co-Buying' && !loading && (
                    <>
                        <div style={{ marginTop: '3rem' }}>
                            <SectionHeader>
                                <SectionTitle>
                                    Pending Co-Op Interests ({coBuyInterests.filter(i => i.status === 'pending').length})
                                </SectionTitle>
                            </SectionHeader>
                            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
                                Review and approve user-submitted interests to create new groups or add them to existing ones.
                            </div>

                            {coBuyInterests.filter(i => i.status === 'pending').length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af', fontStyle: 'italic', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                                    No pending interests at the moment
                                </div>
                            ) : (
                                <Table>
                                    <thead>
                                        <tr>
                                            <Th>Group Name</Th>
                                            <Th>Type</Th>
                                            <Th>Submitted By</Th>
                                            <Th>Neighborhood</Th>
                                            <Th>Bulk Item</Th>
                                            <Th>Target Amount</Th>
                                            <Th>Submitted Date</Th>
                                            <Th>Actions</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {coBuyInterests.filter(i => i.status === 'pending').map((interest) => (
                                            <tr key={interest.firestoreId}>
                                                <Td style={{ fontWeight: 600 }}>{interest.name}</Td>
                                                <Td>
                                                    <Badge status={interest.groupType === 'dynamic' ? 'active' : 'pending'}>
                                                        {interest.groupType === 'dynamic' ? 'Dynamic' : 'Fixed'}
                                                    </Badge>
                                                </Td>
                                                <Td>
                                                    <div style={{ fontSize: '0.85rem' }}>
                                                        <strong>{interest.submittedByName}</strong>
                                                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{interest.submittedByEmail}</div>
                                                    </div>
                                                </Td>
                                                <Td>{interest.neighborhood}</Td>
                                                <Td style={{ fontWeight: 600, color: '#10b981' }}>{interest.bulkItem}</Td>
                                                <Td style={{ fontWeight: 600 }}>₦{Number(interest.targetAmount).toLocaleString()}</Td>
                                                <Td>{new Date(interest.submittedAt).toLocaleDateString()}</Td>
                                                <Td>
                                                    <EditBtn onClick={() => openInterestReview(interest)}>Review</EditBtn>
                                                </Td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </div>
                    </>
                )}

                {/* ── Pending Fixed-Group Item Requests Section (Co-Buying Tab Only) ── */}
                {activeTab === 'Co-Buying' && !loading && (
                    <>
                        <div style={{ marginTop: '3rem' }}>
                            <SectionHeader>
                                <SectionTitle>
                                    Pending Fixed-Group Item Requests ({coBuyGroups.filter(g => (g.pendingItemRequests || []).some(r => r.status === 'pending')).length})
                                </SectionTitle>
                            </SectionHeader>
                            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>
                                Members of fixed (curated) groups request new items here. Approve to add them to the group's food pool, or reject.
                            </div>

                            {coBuyGroups.filter(g => (g.pendingItemRequests || []).some(r => r.status === 'pending')).length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af', fontStyle: 'italic', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                                    No pending item requests
                                </div>
                            ) : (
                                coBuyGroups
                                    .filter(g => (g.pendingItemRequests || []).some(r => r.status === 'pending'))
                                    .map(group => (
                                        <div key={group.firestoreId} style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '1rem', marginBottom: '1rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                                <strong style={{ color: '#111827' }}>{group.name}</strong>
                                                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{group.neighborhood}</span>
                                            </div>
                                            {(group.pendingItemRequests || []).filter(r => r.status === 'pending').map((req, idx) => (
                                                <div key={idx} style={{ borderTop: '1px solid #f1f5f9', paddingTop: '0.75rem', marginTop: '0.75rem' }}>
                                                    <div style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                                                        <strong>{req.requestedByName || 'Member'}</strong>
                                                        <span style={{ color: '#6b7280' }}> requested:</span>
                                                    </div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
                                                        {(req.items || []).map((it, i) => (
                                                            <span key={i} style={{ background: '#d1fae5', color: '#065f46', fontSize: '0.72rem', fontWeight: 600, padding: '3px 10px', borderRadius: '999px' }}>
                                                                {it.productName} · {it.quantity} {it.measureUnit}
                                                            </span>
                                                        ))}
                                                        {(req.items || []).length === 0 && <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>No items</span>}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <button
                                                            onClick={() => handleApproveItemRequest(group, (group.pendingItemRequests || []).indexOf(req))}
                                                            style={{ padding: '0.4rem 0.9rem', background: '#10b981', color: 'white', borderRadius: '6px', fontWeight: 'bold', border: 'none', cursor: 'pointer', fontSize: '0.78rem' }}
                                                        >Approve & Add</button>
                                                        <button
                                                            onClick={() => handleRejectItemRequest(group, (group.pendingItemRequests || []).indexOf(req))}
                                                            style={{ padding: '0.4rem 0.9rem', background: '#fee2e2', color: '#b91c1c', borderRadius: '6px', fontWeight: 'bold', border: 'none', cursor: 'pointer', fontSize: '0.78rem' }}
                                                        >Reject</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ))
                            )}
                        </div>
                    </>
                )}

                {/* ── Tab: News ─────────────────────────────────────────────── */}
                {activeTab === 'News' && (
                    <>
                        <SectionHeader>
                            <SectionTitle>📰 Latest News</SectionTitle>
                            <AddBtn onClick={() => {
                                setEditNews(null);
                                setNewsFormData({ title: '', excerpt: '', url: '', imageSrc: '', category: '', source: '', publishedAt: '', published: true });
                                setNewsFormOpen(true);
                            }}>+ Add News Article</AddBtn>
                        </SectionHeader>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>Loading news articles…</div>
                        ) : newsArticles.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>No news articles yet. Click "Add News Article" to get started.</div>
                        ) : (
                            <Table>
                                <thead>
                                    <tr>
                                        <Th>Image</Th>
                                        <Th>Title</Th>
                                        <Th>Category</Th>
                                        <Th>Source</Th>
                                        <Th>Published Date</Th>
                                        <Th>Status</Th>
                                        <Th>Actions</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {newsArticles.map((item) => (
                                        <tr key={item.firestoreId}>
                                            <Td>
                                                {item.imageSrc
                                                    ? <ThumbImg src={item.imageSrc} alt={item.title} onError={e => { e.target.style.display = 'none'; }} />
                                                    : <span style={{ color: '#d1d5db', fontSize: '1.5rem' }}>📷</span>
                                                }
                                            </Td>
                                            <Td style={{ fontWeight: 600, maxWidth: 220 }}>
                                                <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: '#059669', textDecoration: 'none' }}>
                                                    {item.title}
                                                </a>
                                                {item.excerpt && <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>{item.excerpt.slice(0, 80)}…</div>}
                                            </Td>
                                            <Td>{item.category || '—'}</Td>
                                            <Td>{item.source || '—'}</Td>
                                            <Td style={{ fontSize: '0.8rem' }}>{item.publishedAt ? new Date(item.publishedAt).toLocaleDateString() : '—'}</Td>
                                            <Td>
                                                <Badge status={item.published !== false ? 'success' : 'pending'}>
                                                    {item.published !== false ? 'Published' : 'Draft'}
                                                </Badge>
                                            </Td>
                                            <Td>
                                                <EditBtn onClick={() => {
                                                    setEditNews(item);
                                                    setNewsFormData({
                                                        title: item.title || '',
                                                        excerpt: item.excerpt || '',
                                                        url: item.url || '',
                                                        imageSrc: item.imageSrc || '',
                                                        category: item.category || '',
                                                        source: item.source || '',
                                                        publishedAt: item.publishedAt || '',
                                                        published: item.published !== false
                                                    });
                                                    setNewsFormOpen(true);
                                                }}>Edit</EditBtn>
                                                <DeleteBtn onClick={() => handleDeleteNewsArticle(item)}>Delete</DeleteBtn>
                                            </Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </>
                )}

                {/* ── Tab: Settings ──────────────────────────────────────────── */}
                {activeTab === 'Settings' && (
                    <div style={{ maxWidth: 560 }}>
                        <SectionHeader>
                            <SectionTitle>Application Settings</SectionTitle>
                        </SectionHeader>
                        <div style={{ background: 'white', borderRadius: 12, padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                            <h3 style={{ fontWeight: 700, fontSize: '1rem', color: '#111827', marginBottom: 4 }}>💰 VAT / Tax Rate</h3>
                            <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 20 }}>
                                This percentage will be applied at checkout for all transactions. Changes take effect immediately.
                            </p>
                            <form onSubmit={handleSaveVat} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 6, color: '#374151' }}>
                                        VAT Percentage (%)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        value={vatInput}
                                        onChange={e => setVatInput(e.target.value)}
                                        placeholder="e.g. 7.5"
                                        style={{ ...inputStyle, width: '100%' }}
                                        required
                                    />
                                </div>
                                <button type="submit" style={{ padding: '10px 20px', background: '#10b981', color: 'white', borderRadius: 8, fontWeight: 700, fontSize: '0.9rem', whiteSpace: 'nowrap', border: 'none', cursor: 'pointer' }}>
                                    Save VAT
                                </button>
                            </form>
                            {appSettings.find(s => s.key === 'vat_percentage') && (
                                <p style={{ marginTop: 12, fontSize: '0.8rem', color: '#6b7280' }}>
                                    ✅ Current saved VAT: <strong>{appSettings.find(s => s.key === 'vat_percentage')?.value}%</strong>
                                </p>
                            )}
                        </div>

                        {/* ── Danger Zone: Data Cleanup & Demo Reset ──────────────── */}
                        <div style={{ background: 'white', borderRadius: 12, padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginTop: '2rem', border: '1px solid #fee2e2' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <span role="img" aria-label="file cabinet" style={{ fontSize: '1.25rem' }}>🗄️</span>
                                <h3 style={{ fontWeight: 700, fontSize: '1rem', color: '#991b1b', margin: 0 }}>Data Cleanup & Demo Reset</h3>
                            </div>
                            <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 16 }}>
                                Purge demo foods, categories, locations, transactions, and other test data so you can initialize the store with real products and configurations. Requires explicit confirmation text before deletion.
                            </p>
                            
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '14px 16px', marginBottom: 18 }}>
                                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#991b1b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Current Stored Records
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px 12px', fontSize: '0.78rem', color: '#4b5563' }}>
                                    <div><span role="img" aria-label="package">📦</span> Foods: <strong>{foods.length}</strong></div>
                                    <div><span role="img" aria-label="label">🏷️</span> Categories: <strong>{categories.length}</strong></div>
                                    <div><span role="img" aria-label="location pin">📍</span> Locations: <strong>{servedStates.length}</strong></div>
                                    <div><span role="img" aria-label="convenience store">🏪</span> Markets: <strong>{markets.length}</strong></div>
                                    <div><span role="img" aria-label="star">⭐</span> Featured: <strong>{featured.length}</strong></div>
                                    <div><span role="img" aria-label="credit card">💳</span> Orders: <strong>{orders.length}</strong></div>
                                    <div><span role="img" aria-label="handshake">🤝</span> Co-Buying: <strong>{coBuyGroups.length}</strong></div>
                                    <div><span role="img" aria-label="briefcase">💼</span> Partners: <strong>{partners.length}</strong></div>
                                    <div><span role="img" aria-label="star">⭐</span> Reviews: <strong>{reviews.length}</strong></div>
                                    <div><span role="img" aria-label="newspaper">📰</span> News: <strong>{newsArticles.length}</strong></div>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => openClearModal()}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '10px 20px',
                                    background: '#dc2626',
                                    color: 'white',
                                    borderRadius: 8,
                                    fontWeight: 700,
                                    fontSize: '0.88rem',
                                    border: 'none',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                                }}
                            >
                                <span role="img" aria-label="warning">⚠️</span>
                                <span>Clear / Reset Data...</span>
                            </button>
                        </div>
                    </div>
                )}
            </MainContent>

            {formOpen && (
                <AdminProductForm
                    item={editItem}
                    collectionLabel={activeTab}
                    partners={partners.filter(p => p.partnerType !== 'market' || foods.some(f => f.partnerMarketId === p.firestoreId))}
                    onSave={handleSave}
                    onCancel={() => { setFormOpen(false); setEditItem(null); }}
                />
            )}

            {customerFormOpen && (
                <AdminCustomerForm
                    item={editCustomer}
                    onSave={handleSaveCustomer}
                    onCancel={() => { setCustomerFormOpen(false); setEditCustomer(null); }}
                />
            )}

            {orderFormOpen && (
                <AdminOrderForm
                    item={editOrder}
                    onSave={handleSaveOrder}
                    onCancel={() => { setOrderFormOpen(false); setEditOrder(null); }}
                />
            )}

            {coBuyFormOpen && (
                <AdminCoBuyForm
                    item={editCoBuyGroup}
                    onSave={handleSaveCoBuy}
                    onCancel={() => { setCoBuyFormOpen(false); setEditCoBuyGroup(null); }}
                />
            )}

            {coBuyCreateOpen && (
                <AdminCoBuyCreate
                    onSave={handleCreateCoBuyGroup}
                    onCancel={() => setCoBuyCreateOpen(false)}
                />
            )}

            {interestFormOpen && (
                <AdminInterestForm
                    interest={editInterest}
                    existingGroups={coBuyGroups}
                    onApproveAsNew={handleApproveInterestAsNew}
                    onAddToExisting={handleAddInterestToExisting}
                    onReject={handleRejectInterest}
                    onCancel={() => { setInterestFormOpen(false); setEditInterest(null); }}
                />
            )}

            {blogFormOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
                    <div style={{ width: '100%', maxWidth: 720, background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 20px 45px rgba(0,0,0,0.2)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>{editBlog ? 'Edit Blog Post' : 'Add Blog Post'}</h3>
                            <button onClick={() => { setBlogFormOpen(false); setEditBlog(null); }} style={{ color: '#6b7280', fontWeight: 700 }}>Close</button>
                        </div>
                        <form onSubmit={handleSaveBlog} style={{ display: 'grid', gap: 12 }}>
                            <input required value={blogFormData.title} onChange={(e) => setBlogFormData({ ...blogFormData, title: e.target.value })} placeholder="Title" style={inputStyle} />
                            <textarea required value={blogFormData.description} onChange={(e) => setBlogFormData({ ...blogFormData, description: e.target.value })} placeholder="Description / excerpt" style={{ ...inputStyle, minHeight: 100 }} />
                            <input value={blogFormData.imageSrc} onChange={(e) => setBlogFormData({ ...blogFormData, imageSrc: e.target.value })} placeholder="Image URL" style={inputStyle} />
                            <input value={blogFormData.author} onChange={(e) => setBlogFormData({ ...blogFormData, author: e.target.value })} placeholder="Author" style={inputStyle} />
                            <input value={blogFormData.category} onChange={(e) => setBlogFormData({ ...blogFormData, category: e.target.value })} placeholder="Category" style={inputStyle} />
                            <input required value={blogFormData.link} onChange={(e) => setBlogFormData({ ...blogFormData, link: e.target.value })} placeholder="External blog article URL" style={inputStyle} />
                            <input value={blogFormData.locationText} onChange={(e) => setBlogFormData({ ...blogFormData, locationText: e.target.value })} placeholder="Location text (optional)" style={inputStyle} />
                            <input value={blogFormData.pricingText} onChange={(e) => setBlogFormData({ ...blogFormData, pricingText: e.target.value })} placeholder="Pricing text (optional)" style={inputStyle} />
                            <input value={blogFormData.rating} onChange={(e) => setBlogFormData({ ...blogFormData, rating: e.target.value })} placeholder="Rating (optional)" style={inputStyle} />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
                                <button type="button" onClick={() => { setBlogFormOpen(false); setEditBlog(null); }} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #d1d5db', color: '#374151' }}>Cancel</button>
                                <button type="submit" style={{ padding: '10px 16px', borderRadius: 8, background: '#10b981', color: 'white', fontWeight: 700 }}>Save Blog</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {newsFormOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
                    <div style={{ width: '100%', maxWidth: 680, background: 'white', borderRadius: 16, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflowY: 'auto', maxHeight: '90vh' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <div>
                                <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                                    {editNews ? '✏️ Edit News Article' : '📰 Add News Article'}
                                </h3>
                                <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 4 }}>
                                    The article will link out to the original news source URL.
                                </p>
                            </div>
                            <button onClick={() => { setNewsFormOpen(false); setEditNews(null); }} style={{ color: '#6b7280', fontWeight: 700, fontSize: '1.4rem', lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
                        </div>
                        <form onSubmit={handleSaveNews} style={{ display: 'grid', gap: 14 }}>
                            <div>
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#374151', marginBottom: 4 }}>Headline / Title *</label>
                                <input required value={newsFormData.title} onChange={(e) => setNewsFormData({ ...newsFormData, title: e.target.value })} placeholder="e.g. Tomato prices rise 40% across Lagos markets" style={inputStyle} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#374151', marginBottom: 4 }}>Excerpt / Summary *</label>
                                <textarea required value={newsFormData.excerpt} onChange={(e) => setNewsFormData({ ...newsFormData, excerpt: e.target.value })} placeholder="A short 1-2 sentence summary displayed on the homepage card" style={{ ...inputStyle, minHeight: 80 }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#374151', marginBottom: 4 }}>Article URL * <span style={{ fontWeight: 400, color: '#9ca3af' }}>(link to original news source)</span></label>
                                <input required type="url" value={newsFormData.url} onChange={(e) => setNewsFormData({ ...newsFormData, url: e.target.value })} placeholder="https://www.premiumtimesng.com/..." style={inputStyle} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#374151', marginBottom: 4 }}>Category</label>
                                    <input value={newsFormData.category} onChange={(e) => setNewsFormData({ ...newsFormData, category: e.target.value })} placeholder="e.g. Markets, Agriculture, Policy" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#374151', marginBottom: 4 }}>Source Name</label>
                                    <input value={newsFormData.source} onChange={(e) => setNewsFormData({ ...newsFormData, source: e.target.value })} placeholder="e.g. Premium Times, Punch" style={inputStyle} />
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#374151', marginBottom: 4 }}>Cover Image URL <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                                <input value={newsFormData.imageSrc} onChange={(e) => setNewsFormData({ ...newsFormData, imageSrc: e.target.value })} placeholder="https://..." style={inputStyle} />
                                {newsFormData.imageSrc && (
                                    <img src={newsFormData.imageSrc} alt="preview" style={{ height: 80, borderRadius: 8, marginTop: 8, objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                                )}
                            </div>
                            <div>
                                <label style={{ display: 'block', fontWeight: 600, fontSize: '0.82rem', color: '#374151', marginBottom: 4 }}>Published Date</label>
                                <input type="date" value={newsFormData.publishedAt ? newsFormData.publishedAt.slice(0,10) : ''} onChange={(e) => setNewsFormData({ ...newsFormData, publishedAt: e.target.value })} style={inputStyle} />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <input type="checkbox" id="newsPublished" checked={newsFormData.published} onChange={(e) => setNewsFormData({ ...newsFormData, published: e.target.checked })} style={{ width: 18, height: 18, accentColor: '#10b981' }} />
                                <label htmlFor="newsPublished" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151', cursor: 'pointer' }}>
                                    Published (visible on homepage)
                                </label>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 4 }}>
                                <button type="button" onClick={() => { setNewsFormOpen(false); setEditNews(null); }} style={{ padding: '10px 18px', borderRadius: 8, border: '1px solid #d1d5db', color: '#374151', background: 'white', cursor: 'pointer' }}>Cancel</button>
                                <button type="submit" style={{ padding: '10px 22px', borderRadius: 8, background: 'linear-gradient(135deg,#059669,#10b981)', color: 'white', fontWeight: 700, border: 'none', cursor: 'pointer' }}>
                                    {editNews ? 'Update Article' : 'Publish Article'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <AdminClearDataModal
                isOpen={clearDataModalOpen}
                onClose={() => setClearDataModalOpen(false)}
                onSuccess={handleClearDataSuccess}
                initialTarget={clearDataTarget}
                counts={collectionCounts}
            />
        </Page>
    );
}
